import api from './api';
import { formatJobNumber } from '../utils/jobUtils';

/**
 * Enhanced logging system for debugging job service operations
 * Provides comprehensive debugging information for authentication and API issues
 */

// Logger configuration for different operation types
const logDateOperation = (operation, input, output, message = '') => {
};

// Enhanced authentication error logging with comprehensive debugging
const logAuthError = (operation, errorDetails) => {
  console.error(`[AUTH ERROR - ${operation}]`, {
    ...errorDetails,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    origin: window.location.origin,
    currentPath: window.location.pathname,
    apiBaseURL: api.defaults?.baseURL,
    withCredentials: api.defaults?.withCredentials
  });
  
  // Provide actionable debugging information based on status codes
  if (errorDetails.status === 401) {
    console.warn('[AUTH DEBUG] 401 Unauthorized - Authentication failed:', {
      possibleCauses: [
        'Session cookie expired or missing',
        'User not logged in',
        'Authentication credentials invalid',
        'CORS credentials not being sent properly'
      ],
      recommendations: [
        'Check browser dev tools for authentication cookies',
        'Verify user login status in authService',
        'Ensure withCredentials is set to true for API calls',
        'Try logging out and back in to refresh session'
      ]
    });
  } else if (errorDetails.status === 403) {
    console.warn('[AUTH DEBUG] 403 Forbidden - Access denied:', {
      possibleCauses: [
        'User authenticated but lacks required permissions',
        'Insufficient user role for this operation',
        'Account may be deactivated or restricted'
      ],
      recommendations: [
        'Check user role and permissions',
        'Contact administrator for access rights',
        'Verify account status is active'
      ]
    });
  } else if (errorDetails.status === 404) {
    console.warn('[AUTH DEBUG] 404 Not Found - Endpoint missing:', {
      possibleCauses: [
        'API endpoint does not exist on server',
        'Backend routing configuration issue',
        'Blueprint not registered properly',
        'Server not running or unreachable'
      ],
      recommendations: [
        'Verify backend server is running',
        'Check API endpoint exists in backend routes',
        'Ensure all blueprints are registered',
        'Test endpoint directly with API testing tool'
      ]
    });
  } else if (errorDetails.status === 405) {
    console.warn('[AUTH DEBUG] 405 Method Not Allowed - HTTP method issue:', {
      possibleCauses: [
        'Wrong HTTP method for endpoint (GET vs POST)',
        'Backend authentication redirecting incorrectly',
        'Route handler not configured for this method'
      ],
      recommendations: [
        'Check if correct HTTP method is being used',
        'Verify backend route accepts the request method',
        'Check for authentication redirects causing method changes'
      ]
    });
  } else if (errorDetails.status >= 500) {
    console.error('[AUTH DEBUG] Server Error - Backend issue:', {
      possibleCauses: [
        'Internal server error',
        'Database connection issue',
        'Backend application crash or misconfiguration'
      ],
      recommendations: [
        'Check backend server logs',
        'Verify database connectivity',
        'Restart backend service if necessary',
        'Contact system administrator'
      ]
    });
  } else if (!errorDetails.status) {
    console.error('[AUTH DEBUG] Network Error - Connection issue:', {
      possibleCauses: [
        'Backend server not accessible',
        'Network connectivity problems',
        'CORS preflight request blocked',
        'DNS resolution failure'
      ],
      recommendations: [
        'Check network connection',
        'Verify backend server URL is correct',
        'Test backend accessibility directly',
        'Check CORS configuration on backend'
      ]
    });
  }
};

/**
 * Enhanced authentication testing with multiple fallback strategies
 * Tests user authentication without relying on potentially missing endpoints
 * 
 * @returns {Promise<Object>} Comprehensive authentication status and connectivity info
 */
export const testAuthentication = async () => {

  try {
    // Primary authentication test - check current user status

    const userResponse = await api.get('/auth/me');

    // Authentication confirmed - now test API connectivity with existing endpoint

    // Instead of testing /health (which may not exist), test a lightweight existing endpoint
    // Use a simple endpoint that we know exists from the logs - like /auth/users
    try {
      await api.get('/auth/users');

      return {
        authenticated: true,
        user: userResponse.data,
        apiConnected: true,
        connectivityTest: 'success',
        timestamp: new Date().toISOString()
      };
    } catch (connectivityError) {

      console.debug('[AUTH TEST] Connectivity error details:', {
        status: connectivityError.response?.status,
        statusText: connectivityError.response?.statusText,
        url: connectivityError.config?.url
      });
      
      // User is authenticated but there might be API connectivity issues
      // This is still a success for authentication purposes
      return {
        authenticated: true,
        user: userResponse.data,
        apiConnected: false,
        connectivityTest: 'failed',
        connectivityError: connectivityError.message,
        timestamp: new Date().toISOString()
      };
    }
    
  } catch (authError) {
    console.error('[AUTH TEST] Authentication test failed:', authError);
    
    // Log comprehensive error details for debugging
    logAuthError('AUTH_TEST', {
      status: authError.response?.status,
      statusText: authError.response?.statusText,
      errorData: authError.response?.data,
      requestUrl: authError.config?.url,
      requestMethod: authError.config?.method,
      message: authError.message
    });
    
    return {
      authenticated: false,
      user: null,
      apiConnected: false,
      error: authError.message,
      status: authError.response?.status,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Enhanced retry mechanism with intelligent error handling
 * Provides sophisticated retry logic with authentication recovery
 * 
 * @param {Function} requestFunction - The API request function to retry
 * @param {Array} args - Arguments to pass to the request function
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<any>} Result of the successful request
 */
const retryWithAuth = async (requestFunction, args, maxRetries = 1) => {
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {

      return await requestFunction(...args);
      
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      

      // Handle 401 Unauthorized with authentication retry
      if (status === 401 && attempt < maxRetries) {
        console.warn('[RETRY AUTH] 401 detected, testing authentication for retry...');
        
        const authStatus = await testAuthentication();
        if (authStatus.authenticated) {

          continue; // Retry the request
        } else {
          console.error('[RETRY AUTH] Authentication invalid, cannot retry');
          throw new Error('Authentication required. Please log in again.');
        }
      }
      
      // For other errors or final attempt, don't retry
      if (attempt >= maxRetries) {
        console.error(`[RETRY AUTH] All ${maxRetries + 1} attempts failed`);
        break;
      }
    }
  }
  
  // Handle final error with user-friendly messages
  const status = lastError.response?.status;
  
  if (status === 401) {
    throw new Error('Authentication required. Please log in again.');
  } else if (status === 403) {
    throw new Error('Access denied. You do not have permission for this operation.');
  } else if (status === 404) {
    throw new Error('Service endpoint not found. This may be a backend configuration issue.');
  } else if (status === 405) {
    throw new Error('Request method not allowed. This indicates a backend routing issue.');
  } else if (status >= 500) {
    throw new Error('Server error. Please try again later or contact support.');
  } else if (!lastError.response) {
    throw new Error('Network error. Please check your connection and ensure the backend server is accessible.');
  } else {
    throw lastError; // Re-throw original error if no specific handling applies
  }
};

/**
 * Date formatting utility for consistent YYYY-MM-DD format
 * Handles timezone issues by working with local dates only
 * 
 * @param {Date} date - The date to format
 * @returns {string|null} Formatted date string or null if invalid
 */
function formatDateToYYYYMMDD(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Enhanced date parsing that preserves the intended day
 * Prevents timezone-related day shifting issues
 * 
 * @param {string} dateStr - Date string in various formats
 * @returns {Date|null} Date object set to noon local time or null if invalid
 */
function parseDatePreservingDay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  
  // Handle ISO date strings with time component like 2025-05-30T00:00:00.000Z
  if (dateStr.includes('T')) {
    dateStr = dateStr.split('T')[0];
  }
  
  // Handle date strings like "Fri, 30 May 2025 00:00:00 GMT"
  if (dateStr.includes(',')) {
    try {
      const tempDate = new Date(dateStr);
      const year = tempDate.getUTCFullYear();
      const month = tempDate.getUTCMonth();
      const day = tempDate.getUTCDate();
      return new Date(year, month, day, 12, 0, 0);
    } catch (e) {
      console.error('[DATE PARSE] Error parsing date with comma:', e);
      return null;
    }
  }
  
  // Handle simple YYYY-MM-DD format
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
  }
  
  // Fallback to standard parsing with noon time set
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    date.setHours(12, 0, 0, 0);
    return date;
  } catch (e) {
    console.error('[DATE PARSE] Error in fallback date parsing:', e);
    return null;
  }
}

/**
 * Display-friendly date formatting in MM/DD/YYYY format
 * 
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string or empty string if invalid
 */
function formatDateForDisplay(date) {
  if (!date) return '';
  
  let dateObj = date;
  if (typeof date === 'string') {
    dateObj = parseDatePreservingDay(date);
  }
  
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return '';
  }
  
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${month}/${day}/${year}`;
}

/**
 * Enhanced job retrieval with comprehensive error handling and authentication management
 * Implements robust retry logic and detailed error reporting
 * 
 * @param {Object} params - Optional query parameters for filtering jobs
 * @returns {Promise<Array>} Promise resolving to an array of processed jobs
 */
export const getJobs = async (params = {}) => {

  try {
    // Pre-flight authentication check to identify issues early

    const authStatus = await testAuthentication();
    
    if (!authStatus.authenticated) {
      console.error('[GET JOBS] Pre-flight authentication failed:', authStatus);
      
      // Log the authentication error for debugging
      logAuthError('GET_JOBS_PRECHECK', {
        status: authStatus.status,
        statusText: undefined,
        errorData: undefined,
        requestUrl: '/auth/me',
        requestMethod: 'GET',
        message: authStatus.error
      });
      
      throw new Error('Not authenticated. Please log in again.');
    }
    

    // Make the actual API request with enhanced error handling
    const response = await retryWithAuth(
      async () => {

        // Log detailed request information for debugging
        try {
          const result = await api.get('/jobs', { params });
          return result;
          
        } catch (requestError) {
          console.error('[GET JOBS] Direct API request failed:', {
            status: requestError.response?.status,
            statusText: requestError.response?.statusText,
            errorData: requestError.response?.data,
            message: requestError.message,
            requestUrl: requestError.config?.url,
            requestMethod: requestError.config?.method
          });
          
          // Re-throw with the original error for retry mechanism to handle
          throw requestError;
        }
      },
      [], // No additional arguments for the request function
      2   // Allow up to 2 retry attempts
    );
    
    // Process the response data to handle date formatting issues

    if (!Array.isArray(response.data)) {
      console.warn('[GET JOBS] Response data is not an array:', typeof response.data);
      return [];
    }
    
    const processedJobs = response.data.map((job, index) => {
      try {
        // Create a copy to avoid modifying original data
        const processedJob = { ...job };
        
        // Handle scheduled_date formatting
        if (processedJob.scheduled_date) {
          const originalDate = processedJob.scheduled_date;
          const parsedDate = parseDatePreservingDay(originalDate);
          
          // Add debugging properties
          processedJob._original_scheduled_date = originalDate;
          processedJob._parsed_date_obj = parsedDate;
          processedJob.formatted_date = formatDateForDisplay(parsedDate);
          
          // Log date processing for first few jobs
          if (index < 3) {
            logDateOperation('PROCESS JOB DATE', originalDate, parsedDate, 
              `Job #${formatJobNumber(processedJob.job_number) || processedJob.id}`);
          }
        }
        
        return processedJob;
        
      } catch (processingError) {
        console.error(`[GET JOBS] Error processing job at index ${index}:`, processingError);
        // Return original job if processing fails
        return job;
      }
    });
    
    // Log summary of processed jobs
    return processedJobs;
    
  } catch (error) {
    console.error('[GET JOBS] Job retrieval failed:', error);
    
    // Log comprehensive error information
    logAuthError('GET_JOBS', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    // Provide user-friendly error messages based on error characteristics
    if (error.message === 'Not authenticated. Please log in again.') {
      throw error; // Re-throw authentication errors as-is
    } else if (error.message.includes('Authentication required')) {
      throw new Error('Your session has expired. Please log out and log back in.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to access jobs. Please contact your administrator.');
    } else if (error.response?.status === 404) {
      throw new Error('Jobs service not found. Please ensure the backend server is running and properly configured.');
    } else if (error.response?.status === 405) {
      throw new Error('The jobs service is temporarily unavailable due to a server configuration issue. Please try again later or contact support.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error occurred while retrieving jobs. Please try again later.');
    } else if (!error.response) {
      throw new Error('Unable to connect to the jobs service. Please check your network connection and try again.');
    } else {
      throw new Error(`Failed to load jobs: ${error.message}`);
    }
  }
};

/**
 * Enhanced individual job retrieval with comprehensive error handling
 * 
 * @param {number|string} id - The job ID to retrieve
 * @returns {Promise<Object>} Promise resolving to the processed job data
 */
export const getJob = async (id) => {

  try {
    const response = await retryWithAuth(
      async () => {

        return await api.get(`/jobs/${id}`);
      },
      [],
      2 // Allow 2 retry attempts
    );
    
    const job = response.data;

    // Process date fields if present
    if (job.scheduled_date) {
      const originalDate = job.scheduled_date;
      const parsedDate = parseDatePreservingDay(originalDate);
      
      job._original_scheduled_date = originalDate;
      job._parsed_date_obj = parsedDate;
      job.formatted_date = formatDateForDisplay(parsedDate);
      
      logDateOperation('GET SINGLE JOB', id, originalDate, `Job #${id} date processing`);
      
    }
    
    return job;
    
  } catch (error) {
    console.error(`[GET JOB] Failed to retrieve job #${id}:`, error);
    
    logAuthError('GET_JOB', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    // Provide specific error messages for different scenarios
    if (error.response?.status === 404) {
      throw new Error(`Job #${id} not found. It may have been deleted or you may not have access to it.`);
    } else if (error.response?.status === 403) {
      throw new Error(`Access denied for job #${id}. You may not have permission to view this job.`);
    } else {
      throw error;
    }
  }
};

/**
 * Enhanced job scheduling with comprehensive date handling and error management
 * 
 * @param {number|string} jobId - The job ID to schedule
 * @param {Object} scheduleData - The scheduling information
 * @returns {Promise<Object>} Promise resolving to the scheduled job
 */
export const scheduleJob = async (jobId, scheduleData) => {


  try {
    // Create a deep copy for processing to avoid modifying original data
    const processedData = JSON.parse(JSON.stringify(scheduleData));
    
    // Enhanced date processing with multiple format support
    if (processedData.scheduled_date) {
      const originalDate = processedData.scheduled_date;
      
      // Handle different date input formats
      if (originalDate instanceof Date) {
        processedData.scheduled_date = formatDateToYYYYMMDD(originalDate);

      } else if (typeof originalDate === 'string') {
        // Handle ISO format strings
        if (originalDate.includes('T') || originalDate.includes('Z') || originalDate.includes('+')) {
          const parsedDate = new Date(originalDate);
          processedData.scheduled_date = formatDateToYYYYMMDD(parsedDate);

        } else if (originalDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Already in YYYY-MM-DD format

        } else {
          // Try to parse other string formats
          try {
            const parsedDate = new Date(originalDate);
            if (!isNaN(parsedDate.getTime())) {
              processedData.scheduled_date = formatDateToYYYYMMDD(parsedDate);

            } else {
              console.warn(`[SCHEDULE JOB] Could not parse date string: ${originalDate}`);
            }
          } catch (dateError) {
            console.error(`[SCHEDULE JOB] Date parsing error:`, dateError);
          }
        }
      }
    }
    
    // Handle alternative date field names
    if (processedData.schedule_date && !processedData.scheduled_date) {
      processedData.scheduled_date = processedData.schedule_date;

    }
    

    // Make the scheduling request with retry logic
    const response = await retryWithAuth(
      async () => {

        return await api.post(`/jobs/${jobId}/schedule`, processedData);
      },
      [],
      2 // Allow 2 retry attempts
    );
    

    // Process the response to fix date handling
    const scheduledJob = response.data;
    
    if (scheduledJob.scheduled_date) {
      const parsedDate = parseDatePreservingDay(scheduledJob.scheduled_date);
      scheduledJob._original_scheduled_date = scheduledJob.scheduled_date;
      scheduledJob._parsed_date_obj = parsedDate;
      scheduledJob.formatted_date = formatDateForDisplay(parsedDate);
      
    }
    
    return scheduledJob;
    
  } catch (error) {
    console.error(`[SCHEDULE JOB] Failed to schedule job #${jobId}:`, error);
    
    logAuthError('SCHEDULE_JOB', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    // Enhanced error logging for debugging
    if (error.response) {
      console.error(`[SCHEDULE JOB] Server responded with error:`, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    
    throw error;
  }
};

/**
 * Enhanced job status update with comprehensive authentication and error handling
 * 
 * @param {number|string} jobId - The job ID to update
 * @param {Object} statusData - The status data to update
 * @returns {Promise<Object>} Promise resolving to the updated job
 */
export const updateJobStatus = async (jobId, statusData) => {


  try {
    // Pre-authentication check for status updates

    const authTest = await testAuthentication();
    
    if (!authTest.authenticated) {
      console.error('[UPDATE JOB STATUS] Authentication check failed:', authTest);
      throw new Error('Not authenticated. Please log in again.');
    }
    

    // Make the status update request with retry logic
    const response = await retryWithAuth(
      async () => {

        // Log API configuration for debugging
        const result = await api.put(`/jobs/${jobId}/status`, statusData);
        
        return result;
      },
      [],
      2 // Allow 2 retry attempts
    );
    
    // Process the response to handle dates
    const updatedJob = response.data;
    
    if (updatedJob.scheduled_date) {
      const parsedDate = parseDatePreservingDay(updatedJob.scheduled_date);
      updatedJob._original_scheduled_date = updatedJob.scheduled_date;
      updatedJob._parsed_date_obj = parsedDate;
      updatedJob.formatted_date = formatDateForDisplay(parsedDate);
    }
    
    return updatedJob;
    
  } catch (error) {
    console.error(`[UPDATE JOB STATUS] Failed to update job #${jobId} status:`, error);
    
    logAuthError('UPDATE_JOB_STATUS', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    // Provide specific error messages based on error type
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Your session may have expired. Please log out and log back in.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You may not have permission to update job status.');
    } else if (error.response?.status === 404) {
      throw new Error(`Job #${jobId} not found or may have been deleted.`);
    } else if (!error.response) {
      throw new Error('Network error. Please check your connection and try again.');
    } else {
      throw error;
    }
  }
};

/**
 * Enhanced door completion with comprehensive error handling
 * 
 * @param {number|string} jobId - The job ID
 * @param {number|string} doorId - The door ID to complete
 * @param {Object} completionData - The door completion data
 * @returns {Promise<Object>} Promise resolving to the completion data
 */
export const completeDoor = async (jobId, doorId, completionData) => {


  try {
    const response = await retryWithAuth(
      async () => {

        return await api.post(`/jobs/${jobId}/doors/${doorId}/complete`, completionData);
      },
      [],
      2 // Allow 2 retry attempts
    );
    

    return response.data;
    
  } catch (error) {
    console.error(`[COMPLETE DOOR] Failed to complete door #${doorId} for job #${jobId}:`, error);
    
    logAuthError('COMPLETE_DOOR', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    throw error;
  }
};

/**
 * Enhanced scheduled jobs retrieval with comprehensive date range handling
 * 
 * @param {string|Date} startDate - Start date for range
 * @param {string|Date} endDate - End date for range
 * @param {string|null} region - Optional region filter
 * @returns {Promise<Array>} Promise resolving to array of scheduled jobs
 */
export const getScheduledJobs = async (startDate, endDate, region = null) => {

  try {
    // Format dates consistently for API request
    const formattedStartDate = startDate instanceof Date ? formatDateToYYYYMMDD(startDate) : startDate;
    const formattedEndDate = endDate instanceof Date ? formatDateToYYYYMMDD(endDate) : endDate;
    
    // Build query parameters
    const params = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      status: 'scheduled'
    };
    
    if (region) {
      params.region = region;
    }
    
    const response = await retryWithAuth(
      async () => {

        return await api.get('/jobs', { params });
      },
      [],
      2 // Allow 2 retry attempts
    );
    
    // Process the jobs to handle date formatting
    const scheduledJobs = response.data.map(job => {
      if (job.scheduled_date) {
        const parsedDate = parseDatePreservingDay(job.scheduled_date);
        job._original_scheduled_date = job.scheduled_date;
        job._parsed_date_obj = parsedDate;
        job.formatted_date = formatDateForDisplay(parsedDate);
      }
      return job;
    });
    
    return scheduledJobs;
    
  } catch (error) {
    console.error('[GET SCHEDULED JOBS] Failed to retrieve scheduled jobs:', error);
    
    logAuthError('GET_SCHEDULED_JOBS', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    throw error;
  }
};

/**
 * Enhanced jobs retrieval for specific date with comprehensive filtering
 * 
 * @param {string|Date} date - The date to get jobs for
 * @param {string|null} region - Optional region filter
 * @returns {Promise<Array>} Promise resolving to array of jobs for that date
 */
export const getJobsForDate = async (date, region = null) => {

  try {
    // Format date consistently
    const formattedDate = date instanceof Date ? formatDateToYYYYMMDD(date) : date;
    
    // Build query parameters
    const params = {
      scheduled_date: formattedDate,
      status: 'scheduled'
    };
    
    if (region) {
      params.region = region;
    }
    
    const response = await retryWithAuth(
      async () => {

        return await api.get('/jobs', { params });
      },
      [],
      2 // Allow 2 retry attempts
    );
    
    // Process the jobs to handle date formatting
    const dateJobs = response.data.map(job => {
      if (job.scheduled_date) {
        const parsedDate = parseDatePreservingDay(job.scheduled_date);
        job._original_scheduled_date = job.scheduled_date;
        job._parsed_date_obj = parsedDate;
        job.formatted_date = formatDateForDisplay(parsedDate);
      }
      return job;
    });
    
    return dateJobs;
    
  } catch (error) {
    console.error(`[GET JOBS FOR DATE] Failed to retrieve jobs for date ${date}:`, error);
    
    logAuthError('GET_JOBS_FOR_DATE', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    throw error;
  }
};

/**
 * Enhanced job cancellation with comprehensive error handling and authentication
 * 
 * @param {number} jobId - The ID of the job to cancel
 * @param {Object} cancelData - Optional data containing cancellation details
 * @param {string} cancelData.reason - Optional reason for cancellation
 * @returns {Promise<Object>} - The API response with cancelled job data
 */
export const cancelJob = async (jobId, cancelData = {}) => {


  try {
    // Pre-authentication check for job cancellation

    const authTest = await testAuthentication();
    
    if (!authTest.authenticated) {
      console.error('[CANCEL JOB] Authentication check failed:', authTest);
      throw new Error('Not authenticated. Please log in again.');
    }
    

    // Make the cancellation request with retry logic
    const response = await retryWithAuth(
      async () => {

        const result = await api.post(`/jobs/${jobId}/cancel`, cancelData);
        return result.data;
      },
      [],
      2 // Allow 2 retry attempts
    );
    

    return response;
    
  } catch (error) {
    console.error(`[CANCEL JOB] Failed to cancel job #${jobId}:`, error);
    
    logAuthError('CANCEL_JOB', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });
    
    // Provide specific error messages for cancellation scenarios
    if (error.response?.status === 404) {
      throw new Error(`Job #${jobId} not found or may have already been cancelled.`);
    } else if (error.response?.status === 403) {
      throw new Error(`Access denied. You may not have permission to cancel job #${jobId}.`);
    } else if (error.response?.status === 409) {
      throw new Error(`Job #${jobId} cannot be cancelled in its current state.`);
    } else {
      throw error;
    }
  }
};

/**
 * Create a special job directly without going through the estimate/bid flow
 * This creates a job that can have items added via voice transcription
 *
 * @param {Object} jobData - The job creation data
 * @param {number} jobData.customer_id - The customer ID (required)
 * @param {number} jobData.site_id - The site ID (optional)
 * @param {string} jobData.job_scope - Description of the job scope
 * @param {string} jobData.region - Region code (OC, LA, IE)
 * @returns {Promise<Object>} Promise resolving to the created job data
 */
export const createSpecialJob = async (jobData) => {


  try {
    // Pre-authentication check

    const authTest = await testAuthentication();

    if (!authTest.authenticated) {
      console.error('[CREATE SPECIAL JOB] Authentication check failed:', authTest);
      throw new Error('Not authenticated. Please log in again.');
    }


    const response = await retryWithAuth(
      async () => {

        return await api.post('/jobs/special', jobData);
      },
      [],
      2 // Allow 2 retry attempts
    );


    return response.data;

  } catch (error) {
    console.error('[CREATE SPECIAL JOB] Failed to create special job:', error);

    logAuthError('CREATE_SPECIAL_JOB', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      message: error.message
    });

    if (error.response?.status === 404) {
      throw new Error('Customer or site not found.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.error || 'Invalid job data provided.');
    } else {
      throw error;
    }
  }
};